import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  fetchUserData,
  updateUserProfile,
  uploadProfilePicture,
} from "../redux/authSlice";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import countries from "../GLOBAL/Country";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronLeft, CheckCircle, Shield, ShieldCheck } from "lucide-react";

declare global {
  interface Window { google: any; }
}

/* ─── Design Tokens — Luxury White ─────────────────────── */
const T = {
  bg: "#ffffff",
  surface: "#fafafa",
  surface2: "#f5f5f0",
  card: "#ffffff",
  border: "#e5e5e5",
  border2: "#d4d4d4",
  text: "#0a0a0a",
  textSoft: "#525252",
  muted: "#a3a3a3",
  accent: "#0a0a0a",
  accentSoft: "rgba(0,0,0,0.06)",
  danger: "#b91c1c",
  success: "#166534",
  serif: "'Cormorant Garamond', serif",
  sans: "'Montserrat', sans-serif",
};

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');`;

const SECTIONS = [
  "Personal", "Contact", "Address", "Shipping", "Billing", "Date of Birth", "About"
] as const;

/**
 * Required fields per tab index.
 * Contact tab (1): contactEmail + contactNumber are required; all other contact fields optional.
 * Billing (4) and About (6) are fully optional.
 * DOB tab (5) uses a special "__birthDate__" sentinel.
 */
const TAB_REQUIRED: Record<number, string[]> = {
  0: ["firstName", "middleName", "surName", "username"],
  1: ["contactEmail", "contactNumber"],
  2: ["streetAddress", "apartmentNumber", "postalAddress", "city", "country"],
  3: ["shippingStreetAddress", "shippingApartmentNumber", "shippingPostalAddress", "shippingCity", "shippingCountry"],
  4: [],
  5: ["__birthDate__"],
  6: [],
};

const EditProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, status } = useSelector((state: RootState) => state.auth);
  const loading = status === "loading";

  const [form, setForm] = useState<any>({});
  const [originalForm, setOriginalForm] = useState<any>({});
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [originalBirthDate, setOriginalBirthDate] = useState<Date | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const mainAddressRef = useRef<HTMLInputElement>(null);
  const shippingAddressRef = useRef<HTMLInputElement>(null);

  useEffect(() => { dispatch(fetchUserData()); }, [dispatch]);

  useEffect(() => {
    if (user) {
      const data: any = {
        firstName: user.firstName || "",
        middleName: user.middleName || "",
        surName: user.surName || "",
        username: user.username || "",
        emailAddress: user.emailAddress || "",
        // Contact — primary required fields
        contactEmail: user.contactEmail || "",
        contactNumber: user.contactNumber || "",
        // Contact — optional extras
        workEmail: user.workEmail || "",
        workPhoneNumber: user.workPhoneNumber || "",
        whatsappNumber: user.whatsappNumber || "",
        telegramNumber: user.telegramNumber || "",
        telegramUsername: user.telegramUsername || "",
        userBio: user.userBio || "",
        // Address
        streetAddress: user.address?.streetAddress || "",
        apartmentNumber: user.address?.apartmentNumber || "",
        postalAddress: user.address?.postalAddress || "",
        city: user.address?.city || "",
        state: user.address?.state || "",
        country: user.address?.country || "",
        countryCode: user.address?.countryCode || "",
        zipCode: user.address?.zipCode || "",
        // Shipping
        shippingStreetAddress: user.shippingAddress?.streetAddress || "",
        shippingApartmentNumber: user.shippingAddress?.apartmentNumber || "",
        shippingPostalAddress: user.shippingAddress?.postalAddress || "",
        shippingCity: user.shippingAddress?.city || "",
        shippingState: user.shippingAddress?.state || "",
        shippingCountry: user.shippingAddress?.country || "",
        shippingCountryCode: user.shippingAddress?.countryCode || "",
        shippingZipCode: user.shippingAddress?.zipCode || "",
        // Billing
        billingStreetAddress: user.billingAddress?.streetAddress || "",
        billingCity: user.billingAddress?.city || "",
        billingCountry: user.billingAddress?.country || "",
        billingCountryCode: user.billingAddress?.countryCode || "",
        billingZipCode: user.billingAddress?.zipCode || "",
        iban: user.iban || "",
        bic: user.bic || "",
        taxId: user.taxId || "",
        vatNumber: user.vatNumber || "",
        bankName: user.bankName || "",
      };
      setForm(data);
      setOriginalForm(data);
      const dob = user.birthDate ? new Date(user.birthDate) : null;
      setBirthDate(dob);
      setOriginalBirthDate(dob);
    }
  }, [user]);

  const set = (k: string, v: string) => setForm((p: any) => ({ ...p, [k]: v }));

  /**
   * Select a country and write both name + code using the correct camelCase key.
   * Fix: the old generic prefix approach produced keys like "shippingcountry" (wrong).
   * Now we use explicit key pairs per prefix to ensure correct camelCase.
   */
  const selCountry = (v: string, prefix: "" | "shipping" | "billing" = "") => {
    const c = countries.find(x => x.name === v);
    if (prefix === "") {
      setForm((p: any) => ({ ...p, country: c?.name || "", countryCode: c?.code || "" }));
    } else if (prefix === "shipping") {
      setForm((p: any) => ({ ...p, shippingCountry: c?.name || "", shippingCountryCode: c?.code || "" }));
    } else if (prefix === "billing") {
      setForm((p: any) => ({ ...p, billingCountry: c?.name || "", billingCountryCode: c?.code || "" }));
    }
  };

  /** All form keys that belong to a given tab (for change detection). */
  const getTabFields = (tab: number): string[] => {
    switch (tab) {
      case 0: return ["firstName", "middleName", "surName", "username"];
      case 1: return ["contactEmail", "contactNumber", "workEmail", "workPhoneNumber", "whatsappNumber", "telegramNumber", "telegramUsername"];
      case 2: return ["streetAddress", "apartmentNumber", "postalAddress", "city", "state", "country", "countryCode", "zipCode"];
      case 3: return ["shippingStreetAddress", "shippingApartmentNumber", "shippingPostalAddress", "shippingCity", "shippingState", "shippingCountry", "shippingCountryCode", "shippingZipCode"];
      case 4: return ["billingStreetAddress", "billingCity", "billingCountry", "billingCountryCode", "billingZipCode", "iban", "bic", "taxId", "vatNumber", "bankName"];
      case 6: return ["userBio"];
      default: return [];
    }
  };

  /** Has the current tab changed relative to the original saved state? */
  const tabHasChanged = (tab: number): boolean => {
    if (tab === 5) {
      return (birthDate?.toISOString() || "") !== (originalBirthDate?.toISOString() || "");
    }
    const tabFields = getTabFields(tab);
    return tabFields.some(k => form[k] !== originalForm[k]);
  };

  /** Is the current tab valid (all required fields filled)? */
  const tabIsValid = (tab: number): boolean => {
    const required = TAB_REQUIRED[tab];
    if (required.includes("__birthDate__")) return birthDate !== null;
    return required.every(f => form[f]?.trim?.());
  };

  /** Does a tab have any missing required fields? Used for error dot indicator. */
  const tabHasErrors = (tab: number): boolean => {
    const required = TAB_REQUIRED[tab];
    if (required.length === 0) return false;
    if (required.includes("__birthDate__")) return birthDate === null;
    return required.some(f => !form[f]?.trim?.());
  };

  const err = (k: string) => !form[k]?.trim?.();

  // Completion % across all required fields globally
  const allRequired = [
    ...TAB_REQUIRED[0],
    "contactEmail", "contactNumber",
    ...TAB_REQUIRED[2],
    ...TAB_REQUIRED[3],
  ];
  const filled = allRequired.filter(f => form[f]?.trim?.()).length + (birthDate ? 1 : 0);
  const completion = Math.round((filled / (allRequired.length + 1)) * 100);

  const handleSave = async () => {
    if (!tabHasChanged(activeSection) || !tabIsValid(activeSection)) return;
    await dispatch(updateUserProfile({
      firstName: form.firstName,
      middleName: form.middleName,
      surName: form.surName,
      username: form.username,
      contactEmail: form.contactEmail,
      contactNumber: form.contactNumber,
      workEmail: form.workEmail,
      workPhoneNumber: form.workPhoneNumber,
      whatsappNumber: form.whatsappNumber,
      telegramNumber: form.telegramNumber,
      telegramUsername: form.telegramUsername,
      userBio: form.userBio,
      birthDate: birthDate?.toISOString() || null,
      address: {
        streetAddress: form.streetAddress,
        apartmentNumber: form.apartmentNumber,
        postalAddress: form.postalAddress,
        city: form.city,
        state: form.state,
        country: form.country,
        countryCode: form.countryCode,
        zipCode: form.zipCode,
      },
      shippingAddress: {
        streetAddress: form.shippingStreetAddress,
        apartmentNumber: form.shippingApartmentNumber,
        postalAddress: form.shippingPostalAddress,
        city: form.shippingCity,
        state: form.shippingState,
        country: form.shippingCountry,
        countryCode: form.shippingCountryCode,
        zipCode: form.shippingZipCode,
      },
      billingAddress: {
        streetAddress: form.billingStreetAddress,
        city: form.billingCity,
        country: form.billingCountry,
        countryCode: form.billingCountryCode,
        zipCode: form.billingZipCode,
      },
      iban: form.iban,
      bic: form.bic,
      taxId: form.taxId,
      vatNumber: form.vatNumber,
      bankName: form.bankName,
    }));
    dispatch(fetchUserData());
    setOriginalForm({ ...form });
    setOriginalBirthDate(birthDate);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const ini = form.firstName
    ? form.firstName[0].toUpperCase()
    : form.emailAddress
    ? form.emailAddress[0].toUpperCase()
    : "?";

  const canSave = tabHasChanged(activeSection) && tabIsValid(activeSection);

  return (
    <Box sx={{ minHeight: "100vh", background: T.bg, fontFamily: T.sans }}>
      <style>{FONT_CSS}{RESP_CSS}</style>

      {/* ── HEADER ── */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid #1c1c1c", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, height: 56 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }} onClick={() => navigate(-1)}>
          <ChevronLeft size={20} color="#e8e8e8" />
          <span style={{ color: "#e8e8e8", fontSize: 12, fontWeight: 400, letterSpacing: "0.05em", fontFamily: T.sans }}>Back</span>
        </Box>
        <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", color: "#fff", fontWeight: 300, letterSpacing: "0.3em", fontSize: 14, fontFamily: T.serif }}>Metarelm</span>
        <Box sx={{ width: 60 }} />
      </Box>

      {/* ── MAIN ── */}
      <Box sx={{ maxWidth: 720, mx: "auto", px: { xs: 2, sm: 4 }, pt: 4, pb: 10 }}>

        {/* Profile Card */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4, p: 3, background: T.surface, border: "1px solid " + T.border, position: "relative" }}>
          <label htmlFor="uploadPhoto" style={{ cursor: "pointer", position: "relative", flexShrink: 0 }}>
            <Box sx={{ width: 90, height: 90, borderRadius: "50%", border: "1.5px solid " + T.border2, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: T.surface2 }}>
              {user?.profilePicture
                ? <img src={user.profilePicture} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" />
                : <span style={{ fontSize: 32, fontWeight: 300, color: T.text, fontFamily: T.serif }}>{ini}</span>
              }
            </Box>
            <Box sx={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, borderRadius: "50%", background: T.accent, border: "2px solid " + T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Camera size={12} color={T.bg} />
            </Box>
            <input id="uploadPhoto" type="file" accept="image/*" style={{ display: "none" }} onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              await dispatch(uploadProfilePicture({ file: f }));
              setUploading(false);
              setUploadSuccess(true);
              setTimeout(() => setUploadSuccess(false), 1500);
            }} />
          </label>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 300, color: T.text, fontFamily: T.serif, letterSpacing: "0.03em" }}>
              {form.firstName || "Your"} {form.surName || "Name"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.textSoft, mt: 0.5 }}>{form.emailAddress}</Typography>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 1, px: 1.2, py: 0.4, border: "1px solid " + (user?.isVerified ? T.text : T.border), background: user?.isVerified ? T.accentSoft : "transparent" }}>
              {user?.isVerified ? <ShieldCheck size={11} color={T.text} /> : <Shield size={11} color={T.muted} />}
              <span style={{ fontSize: 9, fontWeight: 500, letterSpacing: "0.12em", color: user?.isVerified ? T.text : T.muted }}>{user?.isVerified ? "VERIFIED" : "UNVERIFIED"}</span>
            </Box>
          </Box>
          {uploadSuccess && <Box sx={{ position: "absolute", top: 12, right: 12 }}><CheckCircle size={20} color={T.success} /></Box>}
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <span style={{ fontSize: 10, color: T.textSoft, letterSpacing: "0.15em", fontFamily: T.sans }}>PROFILE COMPLETION</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: T.text, fontFamily: T.serif }}>{completion}%</span>
          </Box>
          <Box sx={{ height: 3, background: T.border, width: "100%", position: "relative" }}>
            <Box sx={{ height: 3, background: T.accent, width: completion + "%", transition: "width 0.6s ease" }} />
          </Box>
        </Box>

        {/* Section Nav */}
        <Box className="section-nav" sx={{ display: "flex", gap: 0, mb: 4, borderBottom: "1px solid " + T.border, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          {SECTIONS.map((s, i) => {
            const hasErr = tabHasErrors(i);
            const isActive = activeSection === i;
            return (
              <button
                key={s}
                onClick={() => setActiveSection(i)}
                style={{
                  background: "none",
                  border: "none",
                  borderBottom: isActive ? "2px solid " + T.accent : "2px solid transparent",
                  padding: "12px 16px",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? T.text : T.muted,
                  letterSpacing: "0.08em",
                  fontFamily: T.sans,
                  whiteSpace: "nowrap",
                  transition: "all 0.2s",
                  position: "relative",
                }}
              >
                {s.toUpperCase()}
                {hasErr && (
                  <span style={{
                    position: "absolute",
                    top: 8,
                    right: 6,
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: T.danger,
                    display: "block",
                  }} />
                )}
              </button>
            );
          })}
        </Box>

        {/* ── PERSONAL ── */}
        {activeSection === 0 && <>
          <SHead t="Personal Information" s="Legal name as it appears on your identification documents." />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <LField label="Legal First Name" value={form.firstName} onChange={v => set("firstName", v)} error={err("firstName")} required />
            <LField label="Legal Middle Name" value={form.middleName} onChange={v => set("middleName", v)} error={err("middleName")} required />
            <LField label="Legal Surname" value={form.surName} onChange={v => set("surName", v)} error={err("surName")} required />
            <LField label="Username" value={form.username} onChange={v => set("username", v)} error={err("username")} required />
          </Box>
          <LField label="Email Address" value={form.emailAddress} disabled />
        </>}

        {/* ── CONTACT ── */}
        {activeSection === 1 && <>
          <SHead t="Contact Details" s="Primary contact information is required. All other messaging contacts are optional." />

          {/* Required block */}
          <Box sx={{ p: 2.5, background: T.surface, border: "1px solid " + T.border, mb: 3 }}>
            <span style={{ fontSize: 10, color: T.textSoft, letterSpacing: "0.15em", display: "block", marginBottom: 12 }}>PRIMARY CONTACT — REQUIRED</span>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Contact Email" value={form.contactEmail} onChange={v => set("contactEmail", v)} error={err("contactEmail")} required />
              <LField label="Contact Number" value={form.contactNumber} onChange={v => set("contactNumber", v)} error={err("contactNumber")} required />
            </Box>
          </Box>

          {/* Optional extras */}
          <Box sx={{ p: 2.5, background: T.surface, border: "1px solid " + T.border }}>
            <span style={{ fontSize: 10, color: T.textSoft, letterSpacing: "0.15em", display: "block", marginBottom: 12 }}>ADDITIONAL CONTACTS — OPTIONAL</span>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Work Email" value={form.workEmail} onChange={v => set("workEmail", v)} />
              <LField label="Work Phone" value={form.workPhoneNumber} onChange={v => set("workPhoneNumber", v)} />
              <LField label="WhatsApp" value={form.whatsappNumber} onChange={v => set("whatsappNumber", v)} />
              <LField label="Telegram Number" value={form.telegramNumber} onChange={v => set("telegramNumber", v)} />
            </Box>
            <LField label="Telegram Username" value={form.telegramUsername} onChange={v => set("telegramUsername", v)} />
          </Box>
        </>}

        {/* ── ADDRESS ── */}
        {activeSection === 2 && <>
          <SHead t="Residential Address" s="Primary address for your account and correspondence." />
          <LField label="Street Address" value={form.streetAddress} onChange={v => set("streetAddress", v)} error={err("streetAddress")} required inputRef={mainAddressRef} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <LField label="Apartment / Suite" value={form.apartmentNumber} onChange={v => set("apartmentNumber", v)} error={err("apartmentNumber")} required />
            <LField label="P.O. Box" value={form.postalAddress} onChange={v => set("postalAddress", v)} error={err("postalAddress")} required />
            <LField label="City" value={form.city} onChange={v => set("city", v)} error={err("city")} required />
            <LField label="State / Region" value={form.state} onChange={v => set("state", v)} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label="Country *"
              value={form.country || ""}
              onChange={e => selCountry(e.target.value, "")}
              fullWidth
              sx={fsx(!!form.country === false && err("country"))}
            >
              {countries.map(c => (
                <MenuItem key={c.code} value={c.name}>{c.emoji} {c.name}</MenuItem>
              ))}
            </TextField>
            <LField label="Postal / Zip Code" value={form.zipCode} onChange={v => set("zipCode", v)} />
          </Box>
        </>}

        {/* ── SHIPPING ── */}
        {activeSection === 3 && <>
          <SHead t="Shipping Address" s="Delivery address for orders and physical cards." />
          <LField label="Street Address" value={form.shippingStreetAddress} onChange={v => set("shippingStreetAddress", v)} error={err("shippingStreetAddress")} required inputRef={shippingAddressRef} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <LField label="Apartment / Suite" value={form.shippingApartmentNumber} onChange={v => set("shippingApartmentNumber", v)} error={err("shippingApartmentNumber")} required />
            <LField label="P.O. Box" value={form.shippingPostalAddress} onChange={v => set("shippingPostalAddress", v)} error={err("shippingPostalAddress")} required />
            <LField label="City" value={form.shippingCity} onChange={v => set("shippingCity", v)} error={err("shippingCity")} required />
            <LField label="State / Region" value={form.shippingState} onChange={v => set("shippingState", v)} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label="Country *"
              value={form.shippingCountry || ""}
              onChange={e => selCountry(e.target.value, "shipping")}
              fullWidth
              sx={fsx(!!form.shippingCountry === false && err("shippingCountry"))}
            >
              {countries.map(c => (
                <MenuItem key={c.code} value={c.name}>{c.emoji} {c.name}</MenuItem>
              ))}
            </TextField>
            <LField label="Zip Code" value={form.shippingZipCode} onChange={v => set("shippingZipCode", v)} />
          </Box>
        </>}

        {/* ── BILLING & FINANCE ── */}
        {activeSection === 4 && <>
          <SHead t="Billing & Finance" s="Banking and billing details for transactions, invoicing, and compliance. All fields are optional." />
          <Box sx={{ p: 2.5, background: T.surface, border: "1px solid " + T.border, mb: 3 }}>
            <span style={{ fontSize: 10, color: T.textSoft, letterSpacing: "0.15em", display: "block", marginBottom: 12 }}>BILLING ADDRESS</span>
            <LField label="Billing Street Address" value={form.billingStreetAddress} onChange={v => set("billingStreetAddress", v)} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="City" value={form.billingCity} onChange={v => set("billingCity", v)} />
              <LField label="Zip Code" value={form.billingZipCode} onChange={v => set("billingZipCode", v)} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                select
                label="Country"
                value={form.billingCountry || ""}
                onChange={e => selCountry(e.target.value, "billing")}
                fullWidth
                sx={fsx(false)}
              >
                {countries.map(c => (
                  <MenuItem key={c.code} value={c.name}>{c.emoji} {c.name}</MenuItem>
                ))}
              </TextField>
              <Box />
            </Box>
          </Box>
          <Box sx={{ p: 2.5, background: T.surface, border: "1px solid " + T.border, mb: 3 }}>
            <span style={{ fontSize: 10, color: T.textSoft, letterSpacing: "0.15em", display: "block", marginBottom: 12 }}>BANK DETAILS</span>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Bank Name" value={form.bankName} onChange={v => set("bankName", v)} />
              <LField label="IBAN" value={form.iban} onChange={v => set("iban", v)} />
              <LField label="BIC / SWIFT" value={form.bic} onChange={v => set("bic", v)} />
            </Box>
          </Box>
          <Box sx={{ p: 2.5, background: T.surface, border: "1px solid " + T.border }}>
            <span style={{ fontSize: 10, color: T.textSoft, letterSpacing: "0.15em", display: "block", marginBottom: 12 }}>TAX INFORMATION</span>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Tax ID / SSN" value={form.taxId} onChange={v => set("taxId", v)} />
              <LField label="VAT Number" value={form.vatNumber} onChange={v => set("vatNumber", v)} />
            </Box>
          </Box>
        </>}

        {/* ── DOB ── */}
        {activeSection === 5 && <>
          <SHead t="Date of Birth" s="Required for identity verification and regulatory compliance." />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth *"
              value={birthDate}
              onChange={v => setBirthDate(v)}
              slotProps={{ textField: { fullWidth: true, sx: fsx(!birthDate) } }}
            />
          </LocalizationProvider>
        </>}

        {/* ── BIO ── */}
        {activeSection === 6 && <>
          <SHead t="About You" s="A short introduction visible on your public profile. Optional." />
          <TextField
            multiline
            minRows={5}
            label="Short Bio"
            value={form.userBio}
            onChange={e => set("userBio", e.target.value)}
            fullWidth
            sx={fsx(false)}
          />
        </>}

        {/* ── ACTIONS ── */}
        <Box sx={{ mt: 5, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
          <Button
            disabled={!canSave}
            onClick={handleSave}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 0,
              background: canSave ? T.accent : T.border,
              color: canSave ? T.bg : T.muted,
              fontWeight: 500,
              fontSize: 12,
              letterSpacing: "0.2em",
              fontFamily: T.sans,
              "&:hover": { background: canSave ? T.text : T.border },
              "&:disabled": { background: T.border, color: T.muted },
            }}
          >
            {loading ? "SAVING..." : saved ? "SAVED ✓" : "SAVE CHANGES"}
          </Button>
          <Button
            disabled={user?.isVerified}
            onClick={() => !user?.isVerified && navigate("/VerifyIntro/" + user?.userId)}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 0,
              background: user?.isVerified ? "transparent" : T.accent,
              color: user?.isVerified ? T.success : T.bg,
              border: "1px solid " + (user?.isVerified ? T.success : T.accent),
              fontWeight: 500,
              fontSize: 12,
              letterSpacing: "0.2em",
              fontFamily: T.sans,
              "&:hover": { background: user?.isVerified ? "transparent" : "#333" },
            }}
          >
            {user?.isVerified ? "VERIFIED ✓" : "VERIFY ACCOUNT"}
          </Button>
        </Box>

        {/* Tab incomplete hint */}
        {tabHasChanged(activeSection) && !tabIsValid(activeSection) && (
          <Typography sx={{ mt: 1.5, fontSize: 11, color: T.danger, letterSpacing: "0.05em", textAlign: "center" }}>
            Please fill in all required fields on this tab before saving.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default EditProfile;

/* ── Sub-components ── */
const SHead: React.FC<{ t: string; s: string }> = ({ t, s }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontSize: 22, fontWeight: 300, color: T.text, fontFamily: T.serif, letterSpacing: "0.04em", mb: 0.5 }}>{t}</Typography>
    <Typography sx={{ fontSize: 12, color: T.textSoft, lineHeight: 1.6, letterSpacing: "0.02em" }}>{s}</Typography>
  </Box>
);

interface LFieldProps {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}
const LField: React.FC<LFieldProps> = ({ label, value, onChange, disabled, error, required, inputRef }) => (
  <TextField
    label={label}
    value={value || ""}
    onChange={e => onChange?.(e.target.value)}
    fullWidth
    disabled={disabled}
    inputRef={inputRef}
    required={required}
    sx={fsx(error)}
  />
);

const fsx = (hasError?: boolean) => ({
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    fontFamily: T.sans,
    fontSize: 13,
    color: T.text,
    background: T.bg,
    "& fieldset": { borderColor: hasError ? T.danger : T.border },
    "&:hover fieldset": { borderColor: T.text },
    "&.Mui-focused fieldset": { borderColor: T.accent },
    "&.Mui-disabled": { background: T.surface2 },
  },
  "& .MuiInputLabel-root": {
    fontFamily: T.sans,
    fontSize: 12,
    letterSpacing: "0.03em",
    color: T.textSoft,
    "&.Mui-focused": { color: T.accent },
  },
  "& .MuiFormHelperText-root": { fontFamily: T.sans, fontSize: 10 },
});

const RESP_CSS = `
  .section-nav::-webkit-scrollbar{display:none}
  @media(max-width:600px){
    .section-nav button{padding:10px 12px!important;font-size:10px!important}
  }
`;